import { call, fork, put, take, race } from 'redux-saga/effects';
import { takeEvery, eventChannel } from 'redux-saga';
import ComponentsClient from 'rest/ComponentsClient';
import ComponentInstallClient from 'websockets/ComponentInstallClient';
import ComponentUninstallClient from 'websockets/ComponentUninstallClient';
import { errorAction } from 'components/utility/actions';
import * as restartActions from 'components/widgets/Restart/actions';
import * as actions from './actions';
const { types } = actions;

export const installClient = new ComponentInstallClient();
export const uninstallClient = new ComponentUninstallClient();
export const componentsClient = new ComponentsClient();

export function *fetchComponents(action) {
	try {
		const response = yield call([ componentsClient, componentsClient.get ]);
		yield put({ payload: response.data, type: types.FETCH_COMPONENTS_SUCCESS });
	} catch (error) {
		yield put(errorAction(types.FETCH_COMPONENTS_ERROR, error, action));
	}
}

export function createOperationChannel(wsClient, name) {
	// `eventChannel` takes a subscriber function.  The subscriber function
	// takes an `emit` argument to put messages onto the channel
	return eventChannel(emit => {
		wsClient.onStatus(ev => emit(actions.operationUpdate(ev)));
		wsClient.onError(error => emit(actions.operationError({ error, name })));
		wsClient.onClose(() => emit(actions.operationStop(name)));

		// the subscriber must return an unsubscribe function
		// this will be invoked when the saga calls `channel.close` method
		return () => {
			// remove the subscriptions
			wsClient.unregisterEventListeners();
		};
	});
}

export function *startOperation(action) {
	const { operation, packageName } = action.payload;
	try {
		// bind events
		let channel;
		if (operation === 'install') {
			channel = yield call(createOperationChannel, installClient, packageName);
			yield call([ installClient, installClient.connect ]);
			yield call([ installClient, installClient.start ], packageName);
		} else {
			channel = yield call(createOperationChannel, uninstallClient, packageName);
			yield call([ uninstallClient, uninstallClient.connect ]);
			yield call([ uninstallClient, uninstallClient.start ], packageName);
		}

		while (true) {
			// An error from the operationChannel will cause the saga jump to
			// the catch block.  "take" the action from the event that was
			// emitted.
			const action = yield take(channel);
			// now "put" to redux
			yield put(action);
		}
	} catch (error) {
		yield put(actions.operationError({
			operation,
			error,
			name: packageName
		}));
	}
}

export function *cancelInstall() {
	yield call([ installClient, installClient.cancelInstall ]);
}

export function *awaitRestart(action) {
	const { configFile, status, name, operation } = action.payload;

	if (status !== 'installed' && status !== 'uninstalled') {
		return;
	}

	// wait for the websocket to close
	yield take(types.OPERATION_STOP);

	// Trigger a reload
	yield put(restartActions.triggerRestart());

	yield put(actions.operationUpdate({
		name,
		status: 'restarting',
		operation
	}));

	// Wait for the reload to complete
	const [{ success }] = yield [
		race({
			success: take(restartActions.AWAIT_RESTART_SUCCESS),
			error: take(restartActions.AWAIT_RESTART_ERROR),
			timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
		}),
		put(restartActions.awaitRestart({ showDialog: false }))
	];

	if (!success) {
		// it failed to restart.
		yield put(actions.operationUpdate({
			name,
			status: 'failedrestart',
			operation
		}));
		// The user will now have to manually trigger the
		// await restart action after getting the server running and we'll wait
		// indefinitely for it's success here.
		yield take(restartActions.AWAIT_RESTART_SUCCESS);
	}

	// Once the restart is successful, update the install status to success
	yield put(actions.operationUpdate({
		status: 'success',
		name,
		operation,
		configFile
	}));

	// Re-fetch the components list since it will have updated after the install.
	yield put(actions.fetchComponents());
}

export default function *sagas() {
	yield fork(takeEvery, types.FETCH_COMPONENTS, fetchComponents);
	yield fork(takeEvery, types.OPERATION_START, startOperation);
	yield fork(takeEvery, types.OPERATION_UPDATE, awaitRestart);
	yield fork(takeEvery, types.INSTALL_CANCEL, cancelInstall);
}
